import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'store';
import {
  selectImportPillState,
  dismissImportPill,
  setImportModalOpen,
} from 'store/slices/importSlice';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faCheckCircle, faExclamationCircle, faTimes } from '@fortawesome/free-solid-svg-icons';
import './ImportProgressPill.css';

const AUTO_DISMISS_DELAY = 8000;

export const ImportProgressPill = () => {
  const dispatch = useDispatch();
  const { visible, status, percentage } = useSelector(
    (state: RootState) => selectImportPillState(state)
  );

  // Auto-dismiss completed pill after 8 seconds
  useEffect(() => {
    if (status === 'completed' && visible) {
      const timer = setTimeout(() => {
        dispatch(dismissImportPill());
      }, AUTO_DISMISS_DELAY);
      return () => clearTimeout(timer);
    }
  }, [status, visible, dispatch]);

  if (!visible) return null;
  if (status === 'idle') return null;

  const isActive = status === 'pending' || status === 'processing';

  const handleClick = () => {
    dispatch(setImportModalOpen(true));
  };

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    dispatch(dismissImportPill());
  };

  return (
    <div
      className={`import-pill import-pill--${status}`}
      onClick={handleClick}
      title={isActive ? `Import in progress: ${percentage}%` : `Import ${status}`}
    >
      <div className="import-pill__inner">
        {isActive && (
          <div
            className="import-pill__progress-fill"
            style={{ width: `${percentage}%` }}
          />
        )}

        <div className="import-pill__content">
          {isActive ? (
            <>
              <FontAwesomeIcon icon={faSpinner} spin className="import-pill__icon" />
              <span className="import-pill__text">Importing... {percentage}%</span>
            </>
          ) : status === 'completed' ? (
            <>
              <FontAwesomeIcon icon={faCheckCircle} className="import-pill__icon" />
              <span className="import-pill__text">Import Done</span>
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faExclamationCircle} className="import-pill__icon" />
              <span className="import-pill__text">Import Failed</span>
            </>
          )}

          {!isActive && (
            <button
              className="import-pill__dismiss"
              onClick={handleDismiss}
              aria-label="Dismiss import notification"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImportProgressPill;
